import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  draggable,
  dropTargetForElements,
  monitorForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { disableNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/disable-native-drag-preview';
import { FormattedInput, Input, Stack, Text, type Formatter } from '@figma/fpl-components';
import { SwatchButton } from '@figma/fpl-components/beta';
import { Icon16DragHandle } from '@figma/fpl-icons';
import cssVars from '@figma/fpl-tokens/css-vars';
import * as stylex from '@stylexjs/stylex';
import { ColorPickerPopup } from '@/UI/ColorPicker/ColorPickerPopup';
import { PercentFormatter } from '@/UI/NumberInput/percentFormatter';
import { I18N_KEYS } from '@/language/keys';
import {
  formatHexCode,
  parseColorPickerColor,
  parseHexCode,
  parseHexColor,
  parseWebColor,
} from '@/UI/ColorPicker/colorPicker.utils';
import { ToolInputList, ToolInputListRow } from './ToolInputList';
import { rowMarker, styles } from './ToolColorPaletteInput.stylex';
import type { ControlProps, ToolInputRow } from './ToolInputControls';
import type { ComponentProps } from 'react';
import type { Color as WasmColor } from 'web';
import type { WebColor } from '@/UI/ColorPicker/ColorPicker';

const MIN_COLORS = 1;
const MAX_COLORS = 100;
const WHITE: WasmColor = { r: 255, g: 255, b: 255, a: 1.0 };

const TOOL_COLOR_PALETTE_DND_TYPE = 'tool-color-palette-row';

const HEX_FORMATTER: Formatter.WriteFormatter<string> = {
  format: (value) => value,
  parse: (value) => {
    const hex = parseHexCode(value);
    if (!hex) throw new Error('Invalid hex color');
    return hex.slice(1);
  },
};

class PaletteOpacityFormatter extends PercentFormatter {
  protected override formatUnmixed(value: number): string {
    return super.formatUnmixed(value).slice(0, -1);
  }
}

const OPACITY_FORMATTER = new PaletteOpacityFormatter();

type ToolColorPaletteInputProps = Pick<ControlProps, 'id' | 'label' | 'disabled'> &
  Pick<ComponentProps<typeof ToolInputRow>, 'description' | 'required'> & {
    value: { colors: WasmColor[] } | undefined;
    onChange: (value: { colors: WasmColor[] }) => void;
  };

interface RowProps {
  inputId: string;
  index: number;
  color: WasmColor;
  canRemove: boolean;
  disabled: boolean;
  onColorChange: (index: number, next: WebColor) => void;
  onRemove: (index: number) => void;
}

function ColorRow({ inputId, index, color: c, canRemove, disabled, onColorChange, onRemove }: RowProps) {
  const { t } = useTranslation();
  const rootRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLDivElement>(null);
  const [dropEdge, setDropEdge] = useState<'top' | 'bottom'>();
  const [isDragging, setIsDragging] = useState(false);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const hex = formatHexCode(c);

  const handleRgbChange = useCallback(
    (next: Parameters<typeof parseColorPickerColor>[0]) => {
      onColorChange(index, { ...parseColorPickerColor(next), a: c.a });
    },
    [c.a, index, onColorChange],
  );

  const handleHexChange = useCallback((next: string) => handleRgbChange(parseHexColor(`#${next}`)), [handleRgbChange]);

  const handleOpacityChange = useCallback(
    (alpha: number) => onColorChange(index, { ...c, a: alpha }),
    [c, index, onColorChange],
  );

  const togglePicker = useCallback(() => {
    if (!disabled) setIsPickerOpen((open) => !open);
  }, [disabled]);

  const closePicker = useCallback(() => setIsPickerOpen(false), []);

  useEffect(() => {
    if (disabled) {
      setDropEdge(undefined);
      setIsDragging(false);
      setIsPickerOpen(false);
      return;
    }
    const el = rootRef.current;
    const handle = handleRef.current;
    if (!el || !handle) return;

    const cleanupDraggable = draggable({
      element: el,
      dragHandle: handle,
      getInitialData: () => ({ type: TOOL_COLOR_PALETTE_DND_TYPE, inputId, index }),
      onGenerateDragPreview: ({ nativeSetDragImage }) => {
        disableNativeDragPreview({ nativeSetDragImage });
      },
      onDragStart: () => setIsDragging(true),
      onDrop: () => setIsDragging(false),
    });

    const cleanupDropTarget = dropTargetForElements({
      element: el,
      getData: () => ({ type: TOOL_COLOR_PALETTE_DND_TYPE, inputId, index }),
      canDrop: ({ source }) =>
        source.data.type === TOOL_COLOR_PALETTE_DND_TYPE &&
        source.data.inputId === inputId &&
        source.data.index !== index,
      onDragEnter: ({ source }) => {
        setDropEdge(Number(source.data.index) < index ? 'bottom' : 'top');
      },
      onDragLeave: () => setDropEdge(undefined),
      onDrop: () => setDropEdge(undefined),
    });

    return () => {
      cleanupDraggable();
      cleanupDropTarget();
    };
  }, [inputId, index, disabled]);

  return (
    <ToolInputListRow
      ref={rootRef}
      data-tool-color-palette-row="true"
      gap={cssVars.spacer.x8px}
      {...stylex.props(styles.row, rowMarker)}
      remove={{
        label: t(I18N_KEYS.RECIPE_MAIN.NODES.COLOR_PALETTE.REMOVE_ARIA, { hex }),
        disabled: disabled || !canRemove,
        onClick: () => onRemove(index),
      }}
    >
      {isDragging && <div {...stylex.props(styles.dragHighlight)} />}

      {dropEdge && (
        <div {...stylex.props(styles.dropIndicator, dropEdge === 'top' ? styles.dropTop : styles.dropBottom)} />
      )}

      <div
        ref={handleRef}
        {...stylex.props(styles.dragHandle, disabled ? styles.dragHandleDisabled : styles.dragHandleEnabled)}
        aria-label={t(I18N_KEYS.RECIPE_MAIN.NODES.COLOR_PALETTE.REORDER_ARIA)}
      >
        <Icon16DragHandle aria-hidden />
      </div>

      <Stack.Col fill {...stylex.props(styles.fields)}>
        <Input.Group columns="minmax(0, 1fr) 56px" ref={fieldRef}>
          <FormattedInput.Root>
            <SwatchButton
              size="xsm"
              fill={{ type: 'color', color: { r: c.r / 255, g: c.g / 255, b: c.b / 255, a: c.a } }}
              aria-label={t(I18N_KEYS.RECIPE_MAIN.NODES.COLOR_PALETTE.SELECT_COLOR)}
              aria-expanded={isPickerOpen && !disabled}
              disabled={disabled}
              onClick={togglePicker}
            />
            <FormattedInput.Field
              aria-label={t(I18N_KEYS.RECIPE_MAIN.NODES.COLOR_PALETTE.EDIT_ARIA, { hex })}
              value={hex.slice(1)}
              formatter={HEX_FORMATTER}
              onChange={handleHexChange}
              disabled={disabled}
            />
          </FormattedInput.Root>
          <FormattedInput.Root>
            <FormattedInput.Field
              aria-label={t(I18N_KEYS.COMMON_COMPONENTS.COLOR_PICKER.OPACITY_ARIA)}
              value={c.a}
              formatter={OPACITY_FORMATTER}
              onChange={handleOpacityChange}
              disabled={disabled}
            />
            <span aria-hidden {...stylex.props(styles.percentSuffix)}>
              <Text.span variant="body-medium" color={disabled ? 'disabled' : 'secondary'}>
                %
              </Text.span>
            </span>
          </FormattedInput.Root>
        </Input.Group>
        <ColorPickerPopup
          color={parseWebColor(c)}
          onChange={handleRgbChange}
          onChangeEnd={handleRgbChange}
          onClose={closePicker}
          isOpen={isPickerOpen && !disabled}
          triggerRef={fieldRef}
          placement="bottom-start"
          showAlpha={false}
        />
      </Stack.Col>
    </ToolInputListRow>
  );
}

export function ToolColorPaletteInput({
  id,
  label,
  required,
  description,
  value,
  onChange,
  disabled,
}: ToolColorPaletteInputProps) {
  const { t } = useTranslation();
  const colors = useMemo(() => (value?.colors?.length ? value.colors : [WHITE]), [value?.colors]);

  const rowKeysRef = useRef<string[]>([]);
  const nextKeyIdRef = useRef(0);
  const rowKeys = useMemo(() => {
    const next = rowKeysRef.current.slice(0, colors.length);
    while (next.length < colors.length) {
      nextKeyIdRef.current += 1;
      next.push(`tool-color-palette-row-${nextKeyIdRef.current}`);
    }
    rowKeysRef.current = next;
    return next;
  }, [colors]);

  const commit = useCallback((next: WasmColor[]) => onChange({ colors: next }), [onChange]);

  const handleColorChange = useCallback(
    (index: number, next: WebColor) => {
      if (disabled) return;
      const updated = colors.slice();
      updated[index] = next;
      commit(updated);
    },
    [colors, commit, disabled],
  );

  const handleRemove = useCallback(
    (index: number) => {
      if (disabled || colors.length <= MIN_COLORS) return;
      rowKeysRef.current.splice(index, 1);
      const updated = colors.slice();
      updated.splice(index, 1);
      commit(updated);
    },
    [colors, commit, disabled],
  );

  const handleAdd = useCallback(() => {
    if (disabled || colors.length >= MAX_COLORS) return;
    commit([...colors, WHITE]);
  }, [colors, commit, disabled]);

  const handleReorder = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (
        disabled ||
        fromIndex === toIndex ||
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= colors.length ||
        toIndex >= colors.length
      ) {
        return;
      }
      const nextKeys = rowKeysRef.current.slice();
      const [movedKey] = nextKeys.splice(fromIndex, 1);
      nextKeys.splice(toIndex, 0, movedKey);
      rowKeysRef.current = nextKeys;
      const next = colors.slice();
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      commit(next);
    },
    [colors, commit, disabled],
  );

  useEffect(() => {
    if (disabled) return;
    return monitorForElements({
      canMonitor: ({ source }) => source.data.type === TOOL_COLOR_PALETTE_DND_TYPE && source.data.inputId === id,
      onDrop({ source, location }) {
        const target = location.current.dropTargets[0];
        if (!target) return;
        const fromIndex = source.data.index;
        const toIndex = target.data.index;
        if (typeof fromIndex !== 'number' || typeof toIndex !== 'number') return;
        handleReorder(fromIndex, toIndex);
      },
    });
  }, [id, handleReorder, disabled]);

  const canRemove = colors.length > MIN_COLORS;

  return (
    <ToolInputList
      label={label}
      required={required}
      description={description}
      disabled={disabled}
      add={{
        label: t(I18N_KEYS.RECIPE_MAIN.NODES.COLOR_PALETTE.ADD_ANOTHER),
        disabled: colors.length >= MAX_COLORS,
        onClick: handleAdd,
      }}
    >
      {colors.map((c, i) => (
        <ColorRow
          key={rowKeys[i]}
          inputId={id}
          index={i}
          color={c}
          canRemove={canRemove}
          disabled={disabled}
          onColorChange={handleColorChange}
          onRemove={handleRemove}
        />
      ))}
    </ToolInputList>
  );
}
